import { Fragment } from 'react';
import type { BrickInstance } from '../presentation/types';
import { useScene } from '../scene/SceneContext';
import { ArtImage } from '../art/ArtImage';
import { BrickHost } from './host';

export interface GuideRow { group: string; asset: string; term: string; meaning: string }

/** Splits a `Title|text` section; the title is everything before the first `|`. */
export function parseSection(entry: string): { title: string; text: string } {
  const at = entry.indexOf('|');
  return at < 0 ? { title: entry, text: '' } : { title: entry.slice(0, at), text: entry.slice(at + 1) };
}

/** Splits `group|asset:ID|Term|Meaning`; the meaning keeps any later `|`. */
export function parseLegend(entry: string): GuideRow | null {
  const parts = entry.split('|');
  if (parts.length < 4) return null;
  return { group: parts[0]!, asset: parts[1]!, term: parts[2]!, meaning: parts.slice(3).join('|') };
}

/** Paragraphs split on newlines; a paragraph starting with a bullet becomes a list item. */
export function GuideText({ text }: { text: string }) {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  const out: React.ReactNode[] = [];
  let items: string[] = [];
  const flush = () => { if (items.length) { out.push(<ul key={`u${out.length}`}>{items.map((t, i) => <li key={i}>{t}</li>)}</ul>); items = []; } };
  for (const line of lines) {
    if (line.startsWith('• ')) items.push(line.slice(2));
    else { flush(); out.push(<p key={`p${out.length}`}>{line}</p>); }
  }
  flush();
  return <>{out}</>;
}

/** Read-only, static help page. It evaluates no rules and exposes no anchors or commands. */
export function RulesGuide({ inst, seat }: { inst: BrickInstance; seat: string | null }) {
  const { table } = useScene();
  const props = inst.props ?? {};
  const title = props.title as string | undefined;
  const sections = ((props.sections as string[] | undefined) ?? []).map(parseSection);
  const rows = ((props.legend as string[] | undefined) ?? []).map(parseLegend).filter((r): r is GuideRow => !!r);
  const groups: { name: string; rows: GuideRow[] }[] = [];
  for (const row of rows) {
    let g = groups.find(x => x.name === row.group);
    if (!g) groups.push(g = { name: row.group, rows: [] });
    g.rows.push(row);
  }
  const glossary = props.glossary === true ? table.glossary ?? [] : [];
  const slots = (props.slots as BrickInstance[] | undefined) ?? [];
  return <section className="rules-guide" aria-label={title ?? 'How to play'}>
    {title && <h2 className="rules-guide-title">{title}</h2>}
    {sections.map((s, i) => <div key={i} className="rules-guide-section">
      <h3>{s.title}</h3><GuideText text={s.text} />
    </div>)}
    {slots.map((child, i) => <div key={i} className="rules-guide-slot"><BrickHost inst={child} seat={seat} /></div>)}
    {groups.length > 0 && <div className="rules-guide-legend">
      <h3>{(props.legendTitle as string | undefined) ?? 'What the icons mean'}</h3>
      <div className="rules-guide-groups">
        {groups.map(g => <div key={g.name} className="rules-guide-group">
          <h4>{g.name}</h4>
          <dl>{g.rows.map((r, i) => <div key={i} className="rules-guide-row">
            <dt>{r.asset && <ArtImage source={r.asset} className="rules-guide-icon" />}<b>{r.term}</b></dt>
            <dd>{r.meaning}</dd>
          </div>)}</dl>
        </div>)}
      </div>
    </div>}
    {glossary.length > 0 && <div className="rules-guide-glossary">
      <h3>Glossary</h3>
      <dl>{glossary.map((g, i) => <Fragment key={i}><dt>{g.term}</dt><dd>{g.text}</dd></Fragment>)}</dl>
    </div>}
  </section>;
}
